import MuiBaseModal from "../../common/modal/MuiModal";
import { Button, Stack } from "@mui/material";
import UserEditForm from "../../../forms/user-edit-from/UserEditFrom";
import { IUsers } from "../users/UsersTable";
import ModalTable from "../../common/tables/ModalTable";
import { IColumn } from "../contacts-board/table/ContactsTable";
import { FormEvent, ReactNode, useEffect, useState } from "react";
import { UserRoleSchema } from "../../../services/Schemas/UserRole";
import {
  AclRolesListColumnFilterable,
  AclRoleUsersListRequest,
} from "../../../services/Endpoints/roles/RoleUserEndpoint";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { FilterOperatorEnum } from "../../../services/Schemas/ListQueryString";
import errorResolver from "../../../utils/error-resolver";
import { useNotificationContext } from "../../../context/notificationContext";
import DeleteAction from "../../common/delete-action/DeleteAction";
import RoleEditForm from "../../../forms/user-edit-from/RoleEditForm";
import EditAction from "../../common/edit-action/EditAction";

interface IUserEditModalProps {
  onCloseModal: () => void;
  userInfo?: Omit<IUsers, "operations"> | null;
  openModal: Omit<IUsers, "operations"> | false;
}
const columns: IColumn[] = [
  {
    id: "acl_role_caption",
    label: "نقش",
    minWidth: 100,
    align: "center",
  },
  {
    id: "acl_role_description",
    label: "توضیحات",
    minWidth: 100,
    align: "center",
  },
  {
    id: "workspace_id",
    label: "شناسه تخته",
    minWidth: 100,
    align: "center",
  },
  {
    id: "operations",
    label: "عملیات",
    minWidth: 100,
    align: "center",
  },
];

interface IListOfUser {
  acl_role_caption: string;
  acl_role_description: string;
  workspace_id: null | number;
  operations: ReactNode;
}

interface IUpdateInfo {
  user_id: number;
  acl_role_id: number;
  workspace_id: number | null;
  acl_role_user_id: number;
}

export default function UserEditModal({
  onCloseModal,
  userInfo,
  openModal,
}: Readonly<IUserEditModalProps>) {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<IUpdateInfo | null>(null);

  const [listOfUserRole, setListOfUserRole] = useState<IListOfUser[]>([]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };

  const protectedInstance = useProtectedAxiosInstance();

  const handleEdit = ({
    acl_role_id,
    acl_role_user_id,
    user_id,
    workspace_id,
  }: IUpdateInfo) => {
    setUpdateInfo({ acl_role_id, acl_role_user_id, user_id, workspace_id });
    setUpdateModal(true);
  };

  const { setNotification } = useNotificationContext();
  const aclRoleUserRequest = new AclRoleUsersListRequest(protectedInstance);

  const getData = async () => {
    try {
      const {
        data: { total, acl_role_users },
      } = await aclRoleUserRequest.list({
        filters: [
          {
            column: AclRolesListColumnFilterable.USER_ID,
            value: userInfo?.id,
            operator: FilterOperatorEnum.EQUAL,
          },
        ],
      });
      setListOfUserRole(
        acl_role_users.map(({ id, workspace_id, acl_role, user }) => ({
          acl_role_caption: acl_role.caption,
          acl_role_description: acl_role.description,
          workspace_id,
          operations: (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap="4px"
            >
              <EditAction
                handleEdit={() =>
                  handleEdit({
                    acl_role_id: acl_role.id,
                    acl_role_user_id: id,
                    user_id: user.id,
                    workspace_id,
                  })
                }
              />
              <DeleteAction handleDelete={() => handleDelete(id)} />
            </Stack>
          ),
        }))
      );
      setTotal(total);
    } catch (error: any) {
      errorResolver({ error, setNotification });
    }
  };

  useEffect(() => {
    openModal && getData();
  }, [page, rowsPerPage, openModal]);

  const handleDelete = async (id: number) => {
    try {
      const { data } = await aclRoleUserRequest.delete(id.toString());
      await getData();
    } catch (error: any) {
      errorResolver({ error, setNotification });
    }
  };

  return (
    <>
      <MuiBaseModal
        open={!!openModal}
        onClose={() => onCloseModal()}
        title={"نقش ها کاربر"}
        hasCloseIcon
      >
        <Stack gap="6px">
          <Stack direction={"row"} justifyContent="end">
            <Button onClick={() => setOpenCreateModal(true)} variant="active">
              اضافه کردن
            </Button>
          </Stack>
          <ModalTable
            // hasTopPagination
            columns={columns}
            count={total}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rows={listOfUserRole}
            rowsPerPage={rowsPerPage}
          />
        </Stack>
      </MuiBaseModal>
      <MuiBaseModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title={"اختصاص نقش جدید به کاربر"}
        hasCloseIcon
      >
        <UserEditForm
          onClose={() => {
            setOpenCreateModal(false);
            getData();
          }}
          userInfo={userInfo}
        />
      </MuiBaseModal>
      <MuiBaseModal
        open={!!updateModal}
        onClose={() => setUpdateModal(false)}
        title={"ویرایش نقش کاربر"}
        hasCloseIcon
      >
        {updateInfo && (
          <RoleEditForm
            onClose={() => {
              setUpdateModal(false);
              setUpdateInfo(null);
              getData();
            }}
            userInfo={userInfo}
            acl_role_id={updateInfo.acl_role_id.toString()}
            acl_role_user_id={updateInfo.acl_role_user_id.toString()}
            workspace_id={updateInfo.workspace_id?.toString()}
          />
        )}
      </MuiBaseModal>
    </>
  );
}
